import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { formatAgentOptions } from '../../../api/agents/formatters'
import agents from '../../../api/agents/hooks'
import { formatContactOptions } from '../../../api/contacts/formatters'
import { Contact } from '../../../api/contacts/types'
import { Owner } from '../../../api/globalTypes'
import { MARKET_SPECIFIC_FIELDS, propertyTransactionsFieldsConfig } from '../../../api/properties/formValuesConfig'
import properties from '../../../api/properties/hooks'
import settings from '../../../api/settings/hooks'
import { propertyTransactionsFields, realEstateGeneralFields } from '../../../common/forms/generalRealEstate/fields'
import { selectPropertyMainRegisterFilter, selectPropertyRegistryDepartmentKpuFilter } from '../../../store/formFilters'
import { mapValuesToFormFields } from '../../../utils/formUtils/forms'
import { splitPropertyValueFields } from '../utils'

const TRANSACTION_COMMISSION_TYPE_OPTIONS = [
    { value: '€', label: '€' },
    { value: '%', label: '%' },
]

export const usePropertyTransactionsFormSelectOptions = (owner?: Owner[], transactionContacts?: Contact[]) => {
    const { propertyMainRegisterId, propertyRegistryDepartmentKpuId } = useSelector((state) => ({
        propertyMainRegisterId: selectPropertyMainRegisterFilter(state),
        propertyRegistryDepartmentKpuId: selectPropertyRegistryDepartmentKpuFilter(state),
    }))

    const { data: applicationMarket } = settings.GetApplicationSettings.useQuery({
        select: (data) => data.applicationMarket,
    })

    const marketName = applicationMarket?.name
    const marketSpecificFields = useMemo(() => MARKET_SPECIFIC_FIELDS[marketName ?? ''] || [], [marketName])
    const excludeMarketSpecificFields = useMemo(() => Object.values(MARKET_SPECIFIC_FIELDS).flat(), [])

    const formFilters = useMemo(() => {
        if (!propertyMainRegisterId && !propertyRegistryDepartmentKpuId) return undefined

        return { propertyMainRegister: propertyMainRegisterId, propertyRegistryDepartmentKpu: propertyRegistryDepartmentKpuId }
    }, [propertyMainRegisterId, propertyRegistryDepartmentKpuId])

    const { transactionStaticFields, transactionDynamicFields } = useMemo(
        () => splitPropertyValueFields(excludeMarketSpecificFields),
        [excludeMarketSpecificFields]
    )

    const { data: staticFormValues, isPending: isStaticFormValuesPending } = properties.FormValues.useQuery(transactionStaticFields)
    const { data: dynamicFormValues, isPending: isDynamicFormValuesPending } = properties.FormValues.useQuery(transactionDynamicFields, formFilters, {
        enabled: Boolean(formFilters),
    })
    const { data: marketFormValues, isPending: isMarketFormValuesPending } = properties.FormValues.useQuery(marketSpecificFields, undefined, {
        enabled: marketSpecificFields.length > 0,
    })
    const { data: agentsData, isPending: isAgentsPending } = agents.GetAgents.useQuery()

    const transactionFormValues = useMemo(
        () => ({
            ...staticFormValues,
            ...dynamicFormValues,
            ...marketFormValues,
        }),
        [staticFormValues, dynamicFormValues, marketFormValues]
    )

    const mappedTransactionValues = useMemo(() => {
        return mapValuesToFormFields(transactionFormValues, propertyTransactionsFieldsConfig)
    }, [transactionFormValues])

    const selectOptions = useMemo(
        () => ({
            ...mappedTransactionValues,
            [propertyTransactionsFields.CONTACT_ID]: formatContactOptions(transactionContacts),
            [realEstateGeneralFields.OWNER_ID]: formatContactOptions(owner),
            [propertyTransactionsFields.PROPERTY_TRANSACTION_AGENT_ID]: formatAgentOptions(agentsData),
            [propertyTransactionsFields.COMMISION_BUYER_TYPE_ID]: TRANSACTION_COMMISSION_TYPE_OPTIONS,
            [propertyTransactionsFields.COMMISION_SELLER_TYPE_ID]: TRANSACTION_COMMISSION_TYPE_OPTIONS,
        }),
        [owner, transactionContacts, agentsData, mappedTransactionValues]
    )

    return {
        selectOptions,
        isPending:
            isStaticFormValuesPending ||
            (Boolean(formFilters) && isDynamicFormValuesPending) ||
            (marketSpecificFields.length > 0 && isMarketFormValuesPending) ||
            isAgentsPending,
    }
}
